import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from 'styles/colors';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { fetchLatestTrending, fetchLatestFilmReel, TrendingItem } from '../../../services/contentfulApi';
import TrendingSkeleton from '__mocks__/skeletonLoading/TrendingSkeleton';

const Trendings = () => {
  const [filmReelItems, setFilmReelItems] = useState<TrendingItem[]>([]);
  const [trendingData, setTrendingData] = useState<TrendingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrendingData = async () => {
      try {
        setLoading(true);
        const [filmReel, trending] = await Promise.all([fetchLatestFilmReel(), fetchLatestTrending()]);
        setFilmReelItems(filmReel);
        setTrendingData(trending);
        if (!filmReel && trending.length === 0) {
          setError('Failed to load trending articles');
        } else {
          setError(null);
        }
      } catch (err) {
        console.error('Failed to load trending data:', err);
        setError('Failed to load trending articles');
      } finally {
        setLoading(false);
      }
    };

    loadTrendingData();
  }, []);

  const handleClick = (link: string) => {
    window.open(link, '_blank');
  };

  if (loading) {
    return (
        <Box>
        <TrendingSkeleton count={6} />
        </Box>
    );
  }

  if (error && filmReelItems.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.text.approxSuvaGrey,
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (trendingData.length === 0 && filmReelItems.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.text.approxSuvaGrey,
            textAlign: 'center',
          }}
        >
          No trending articles available
        </Typography>
      </Box>
    );
  }

  const displayTrending = trendingData.slice(0, 4);
  const allItems = [...filmReelItems, ...displayTrending];

  const renderCard = (item: TrendingItem, index: number, isLast: boolean) => {
    const sourceLabel = item.contentType === 'film' ? 'Film Reel' : 'Independent Press';
    const sourceUrl = item.contentType === 'film' ? 'https://filmreel.com' : 'https://independentpress.com';

    return (
      <Box
        key={item.id}
        sx={{
          display: 'flex',
          gap: 1.5,
          pb: 2.5,
          borderBottom: isLast ? 'none' : '1px solid #e0e0e0',
          alignItems: 'flex-start',
        }}
      >
        {item.imageUrl && (
          <Box
            onClick={() => handleClick(item.link)}
            sx={{
              flexShrink: 0,
              width: '110px',
              height: '90px',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#f0f0f0',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={item.imageUrl}
              alt={item.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <Typography
            onClick={() => handleClick(item.link)}
            sx={{
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontSize: 14,
              fontWeight: 700,
              color: '#000',
              lineHeight: '1.3',
              mb: 0.5,
              cursor: 'pointer',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              '&:hover': { color: '#666' },
            }}
          >
            {item.title}
          </Typography>

          {item.authorName && (
            <Typography
              sx={{
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                fontSize: 11,
                color: '#666',
                mb: 0.5,
              }}
            >
              {item.authorName}
            </Typography>
          )}

          <Typography
            component="a"
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontSize: 11,
              color: colors.text.primaryTheme,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {sourceLabel} ↗
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: '45px',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'fixed',
          zIndex: 999,
          background: 'white',
          height: 40,
          width: '70%',
          top:0,
          mb:2,
        }}
      >
        <TrendingUpIcon sx={{ color: colors.text.primaryTheme, fontSize: 18 }} />
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: colors.text.primaryBlack,
          }}
        >
          Trending Content
        </Typography>
      </Box>

      {allItems.map((item, index) => renderCard(item, index, index === allItems.length - 1))}
    </Box>
  );
};

export default Trendings;